import { useEffect, useState, useMemo } from "react";
import { GetTags } from "../../../services/OiService";
import { FetchPoiCustomTags, upsertSettings } from "../../../services/UserSettingService";
import { Delete16Filled } from "@fluentui/react-icons";
import { useEventReport } from "../redux/ReduxContext";

export interface OiTagsData {
    selectedTags: string[];
}
export type CustomTags = Record<string, string[]>;

interface Tag {
    name: string;
    isCustom: boolean;
    isToggled: boolean;
}

const getTagKey = (tag: string) => tag.trim().toLowerCase();

const normalizeTagNames = (tags: string[]) =>
    tags.flatMap(tag =>
        tag.split(",").map(value => value.trim()).filter(value => value !== "")
    );

const buildTags = (defaultTags: string[], customTags: string[], selectedTags: string[]): Tag[] => {
    const selectedTagKeys = new Set(selectedTags.map(getTagKey));
    const mergedTags = new Map<string, Tag>();

    const upsertTag = (name: string, isCustom: boolean) => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            return;
        }

        const key = getTagKey(trimmedName);
        const existing = mergedTags.get(key);

        mergedTags.set(key, {
            name: existing?.name ?? trimmedName,
            isCustom: Boolean(existing?.isCustom || isCustom),
            isToggled: selectedTagKeys.has(key)
        });
    };

    defaultTags.forEach(tag => upsertTag(tag, false));
    customTags.forEach(tag => upsertTag(tag, true));
    selectedTags.forEach(tag => upsertTag(tag, false));

    return Array.from(mergedTags.values());
};

export default function OiTags() {
    const { state, dispatch } = useEventReport();
    const { testSelection, tags } = state;
    const selectedTags = useMemo(() => normalizeTagNames(tags || []), [tags]);
    const [isLoading, setIsLoading] = useState(false);
    const [newTag, setNewTag] = useState("");
    const [fetchedTags, setFetchedTags] = useState<string[]>([]);
    const [customTags, setCustomTags] = useState<CustomTags>({});
    const [allTags, setAllTags] = useState<Tag[]>([]);
    const currentCustomTags = useMemo(
        () => (testSelection ? customTags[testSelection] ?? [] : []),
        [customTags, testSelection]
    );

    useEffect(() => {
        const fetchTags = async () => {
            if (!testSelection) {
                setFetchedTags([]);
                return;
            }

            setIsLoading(true);
            try {
                const fetchedTagNames = await GetTags(testSelection);
                const poiCustomTags = await FetchPoiCustomTags(testSelection);
                setFetchedTags(fetchedTagNames ?? []);
                setCustomTags(prev => ({
                    ...prev,
                    [testSelection]: poiCustomTags ?? []
                }));

            } catch (error) {
                console.error("Failed to fetch tags:", error);

            } finally {
                setIsLoading(false);
            }
        };

        fetchTags();
    }, [testSelection]);

    useEffect(() => {
        if (!testSelection) {
            setAllTags([]);
            return;
        }

        setAllTags(buildTags(fetchedTags, currentCustomTags, selectedTags));
    }, [currentCustomTags, fetchedTags, selectedTags, testSelection]);


    const handleTagToggle = (tagName: string) => {
        const tagKey = getTagKey(tagName);
        const nextSelectedTags = selectedTags.some(tag => getTagKey(tag) === tagKey)
            ? selectedTags.filter(tag => getTagKey(tag) !== tagKey)
            : [...selectedTags, tagName.trim()];

        dispatch({ type: 'SET_FIELD', payload: { field: 'tags', value: nextSelectedTags } });
    };

    const handleAddNewTag = () => {
        const trimmedTag = newTag.trim();
        const selectionKey = testSelection ?? "";

        if (!trimmedTag || !selectionKey) {
            return;
        }

        const tagKey = getTagKey(trimmedTag);
        const tagAlreadySelected = selectedTags.some(tag => getTagKey(tag) === tagKey);
        const tagAlreadyExists = allTags.some(tag => getTagKey(tag.name) === tagKey);

        if (tagAlreadySelected) {
            setNewTag("");
            return;
        }

        dispatch({ type: 'SET_FIELD', payload: { field: 'tags', value: [...selectedTags, trimmedTag] } });

        if (!tagAlreadyExists) {
            const nextCustomTags = {
                ...customTags,
                [selectionKey]: [...(customTags[selectionKey] ?? []), trimmedTag]
            };

            setCustomTags(nextCustomTags);
            upsertSettings<CustomTags>("poiTags", nextCustomTags);
        }

        setNewTag("");
    };

    function removeTag(value: string) {
        const selectionKey = testSelection ?? "";
        if (!selectionKey) {
            return;
        }

        const nextCustomTags = {
            ...customTags,
            [selectionKey]: (customTags[selectionKey] ?? []).filter(tag => getTagKey(tag) !== getTagKey(value))
        };

        setCustomTags(nextCustomTags);
        dispatch({
            type: 'SET_FIELD',
            payload: {
                field: 'tags',
                value: selectedTags.filter(tag => getTagKey(tag) !== getTagKey(value))
            }
        });

        if (allTags.some(tag => getTagKey(tag.name) === getTagKey(value))) {
            upsertSettings<CustomTags>("poiTags", nextCustomTags);
        }
    }

    const handleNewTagKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddNewTag();
        }
    };

    if (!testSelection) {
        return (
            <div className="bg-white dark:bg-oi-dark-background p-4 mb-4">
                <h6 className="font-semibold mb-4">Tags</h6>
                <p className="text-gray-500 text-sm">Please select a test instrument first to view available tags.</p>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-oi-dark-background p-4 mb-4 drop-shadow-md">
            <h6 className="mb-4">Tags</h6>
            <div className="flex gap-2 w-full">
                <div className="relative w-full">
                    <input
                        type="text"
                        placeholder="ie. grain size"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={handleNewTagKeyPress}
                        className="h-12 w-full p-2 pr-20 mt-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 border bg-primary-light dark:bg-primary-dark border-gray-300 dark:border-gray-600 placeholder-gray-400"
                    />
                    <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-oi-button-blue text-primary-light mt-1 h-8 "
                        onClick={handleAddNewTag}
                    >
                        Add
                    </button>
                </div>
            </div>

            {isLoading && (
                <div className="text-center py-4">
                    <p className="text-sm text-gray-500">Loading tags...</p>
                </div>
            )}

            {!isLoading && (
                <div className="mt-4">
                    {allTags.length === 0 ? (
                        <p className="text-gray-500 text-sm">No tags available for this test instrument.</p>
                    ) : (
                        <div className="flex flex-wrap gap-3">
                            {allTags.map((tag, index) => (
                                <label
                                    key={index}
                                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={tag.isToggled}
                                        onChange={() => handleTagToggle(tag.name)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300 select-none">
                                        {tag.name}
                                    </span>
                                    {tag.isCustom &&
                                        <button onClick={() => removeTag(tag.name)}>
                                            <Delete16Filled color="red" />
                                        </button>
                                    }
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}